import React from 'react';
import { useScrollResetter, useAdobeAnalytics } from 'packages/smart-home-support/hooks';
import { Heading1, BodyText, Subtitle, Detail, FinePrint } from 'packages/smart-home-support/components/Typography';
import styled from 'styled-components';
import BREAK_POINTS from 'packages/smart-home-support/styling/media-queries';
import { Link } from 'react-router-dom';
import { CallToAction } from 'packages/smart-home-support/styling/styles';
import { SectionTitleProps, LabeledIconGridProps, LabeledIconProps } from './models';
import { sections } from './data';
import { ctaProps } from 'packages/smart-home-support/constants';
import getPartnerAsset, { DynamicAssetsKeys } from 'packages/smart-home-support/assets';
import { Container } from '../signup/components/Common';
import { PartnerContent } from 'packages/smart-home-support/partner';

const isCtaInverted: boolean = getPartnerAsset(DynamicAssetsKeys.whatsCoveredCallToActionInverted);

const PageTitle = styled(Heading1)`
  margin-top: 20px;
`;

const Intro = styled(BodyText)`
  margin-top: 15px;
  margin-bottom: 65px;
`;
const TitledContainer = styled.div`
  margin-bottom: 100px;
`;

const Underlined = styled.div`
  display: flex;
  width: 100%;
  border-bottom: none;
  flex-direction: column;
  margin-bottom: 50px;
  @media ${BREAK_POINTS.tablet} {
    flex-direction: row;
    border-bottom: 1px solid #1b1b1b;
    padding-bottom: 10px;
  }
`;

const SectionTitle = styled(Subtitle)`
  text-transform: uppercase;
  font-weight: bold;
`;

const Details = styled(BodyText)``;

const Separator = styled(BodyText)`
  display: none;
  @media ${BREAK_POINTS.tablet} {
    margin-right: 15px;
    margin-left: 15px;
    display: inherit;
  }
`;

const LabeledIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 120px;
  padding: 3%;
  padding-bottom: 20px;
  @media ${BREAK_POINTS.mobileBig} {
    width: 140px;
  }
  @media ${BREAK_POINTS.tablet} {
    width: 160px;
  }
  @media ${BREAK_POINTS.desktop} {
    width: 180px;
  }
`;

const DeviceIcon = styled.img`
  height: 65px;
  width: 65px;
  margin-bottom: 15px;
  @media ${BREAK_POINTS.tablet} {
    height: 75px;
    width: 75px;
  }
`;

const DeviceLabel = styled(Detail)`
  font-weight: bold;
  text-align: center;
  max-width: 100%;
  flex-shrink: 1;
`;

const FinePrintLabel = styled(FinePrint)`
  font-style: italic;
  margin-top: 5px;
  text-align: center;
`;

const Grid = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  @media ${BREAK_POINTS.mobileBig} {
    justify-content: flex-start;
  }
`;

const CTAContainer = styled.div<{ inverted: boolean }>`
  height: 310px;
  padding-right: 10px;
  padding-left: 10px;
  background-color: ${(props) => props.inverted ? props.theme.colors.primaryAccent : props.theme.colors.featureSection1};
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  text-align: center;
  @media screen {
    height: 230px;
  }
`;

const CallToActionButton = styled(Link) <{ inverted: boolean }>`
  ${CallToAction}
  background-color: ${(props) => (props.inverted ? '#fff' : props.theme.colors.primaryAccent)};
  text-transform: ${(props) => props.theme.styles.callToActionTextTransform};
  font-weight: 600;
`;

const FinePrintSection = styled(FinePrint)`
  font-style: italic;
  line-height: 16px;
  margin-top: 20px;
  margin-bottom: 5px;
  @media ${BREAK_POINTS.desktop} {
    margin-top: 40px;
  }
`;

const TitledSection: React.FC<SectionTitleProps> = ({ title, details, children }) => (
  <TitledContainer>
    <Underlined>
      <SectionTitle>{title}</SectionTitle>
      <Separator>|</Separator>
      <Details>{details}</Details>
    </Underlined>
    {children}
  </TitledContainer>
);

const LabeledIcon: React.FC<LabeledIconProps> = ({ icon, label, finePrint }) => (
  <LabeledIconContainer>
    <DeviceIcon src={icon} />
    <DeviceLabel>{label}</DeviceLabel>
    {finePrint && <FinePrintLabel>{finePrint}</FinePrintLabel>}
  </LabeledIconContainer>
);

const LabeledIconGrid: React.FC<LabeledIconGridProps> = ({ devices }) => (
  <Grid>
    {devices.map((device, i) => (
      <LabeledIcon key={i} icon={device.icon} label={device.label} finePrint={device.finePrint} />
    ))}
  </Grid>
);

const Title: React.FC<any> = getPartnerAsset(DynamicAssetsKeys.whatsCoveredCallToActionComponent);
const title: string = getPartnerAsset(DynamicAssetsKeys.whatsCoveredCallToActionTitle);
const homeCallToAction = getPartnerAsset(DynamicAssetsKeys.homeCallToAction);

const CallToActionSection: React.FC<{ inverted: boolean }> = (props) => (
  <CTAContainer inverted={props.inverted}>
    <Title inverted={props.inverted}>{title}</Title>
    <CallToActionButton {...ctaProps} inverted={props.inverted}>
      {homeCallToAction}
    </CallToActionButton>
  </CTAContainer>
);

const WhatsCoveredOriginal: React.FC = () => {
  useScrollResetter();
  useAdobeAnalytics();

  return (
    <>
      <Container>
        <PageTitle>What&rsquo;s Covered?</PageTitle>
        <Intro>
          Most homes have more tech than they realize, and we cover it with one simple plan. No matter how many of these
          eligible products you own, or plan to own, we've got your back.
          <br />
          <br />
          Check out the full list:
        </Intro>

        {sections.map((section, i) => (
          <TitledSection key={i} title={section.title} details={section.details}>
            <LabeledIconGrid devices={section.devices} />
          </TitledSection>
        ))}

        <PartnerContent TechCoachHome="" Verizon="">
          <CallToActionSection inverted={isCtaInverted} />
        </PartnerContent>

        <FinePrintSection>
          *PCs eligible for coverage under this plan are those equipped with a: Windows Operating System version Windows
          7 or newer, Android version 1.6 or newer, and Apple computers which are equipped with an Apple operating
          system version OS X (10) or newer.
        </FinePrintSection>
      </Container>
    </>
  );
};

export default WhatsCoveredOriginal;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/whats-covered/WhatsCoveredOriginal.tsx